import { Injectable, signal } from '@angular/core';

/** トーストの種別 */
export type ToastType = 'success' | 'error' | 'info';

/** 1 件のトースト通知 */
export interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

/**
 * アプリ全体のトースト通知を管理するサービス。
 * root 提供のため、画面遷移直前に発火したトーストも遷移先で表示され続ける
 * （保存 → 一覧へリダイレクトという本アプリの流れに適合する）。
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  /** トースト ID の採番用カウンタ */
  private nextId = 0;

  private readonly _toasts = signal<Toast[]>([]);
  /** 表示中のトースト一覧（読み取り専用） */
  readonly toasts = this._toasts.asReadonly();

  /** 自動クローズ用タイマー（手動クローズ時に解除するため保持） */
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  /** 成功トーストを表示する */
  success(message: string, durationMs = 4000): void {
    this.show('success', message, durationMs);
  }

  /** エラートーストを表示する */
  error(message: string, durationMs = 6000): void {
    this.show('error', message, durationMs);
  }

  /** 情報トーストを表示する */
  info(message: string, durationMs = 4000): void {
    this.show('info', message, durationMs);
  }

  /** 指定 ID のトーストを閉じる */
  dismiss(id: number): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    this._toasts.update((list) => list.filter((t) => t.id !== id));
  }

  private show(type: ToastType, message: string, durationMs: number): void {
    const id = ++this.nextId;
    this._toasts.update((list) => [...list, { id, type, message }]);
    // durationMs 経過で自動クローズ（0 以下なら自動クローズしない）
    if (durationMs > 0) {
      this.timers.set(
        id,
        setTimeout(() => this.dismiss(id), durationMs),
      );
    }
  }
}
